import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            address: '',
            service: '',
            message: ''
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(true);
      setTimeout(() => setError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FAFBFC] to-white pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 fade-in">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A2846] mb-6 sm:mb-8 tracking-tight px-2">
            Kontaktieren Sie uns
          </h1>
          <div 
            className="w-24 sm:w-32 h-2 mx-auto mb-6 sm:mb-10 rounded-full"
            style={{ 
              background: 'linear-gradient(90deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
              boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
            }} 
          />
          <p className="text-lg sm:text-xl lg:text-2xl text-[#1F2937] leading-relaxed max-w-3xl mx-auto font-light px-4">
            Wir erstellen Ihnen ein individuelles Angebot – unverbindlich und kostenfrei
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-elegant">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <div className="flex items-center">
                    <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                    <p className="text-red-700">Es gab einen Fehler. Bitte versuchen Sie es erneut oder rufen Sie uns an.</p>
                  </div>
                </div>
              )}
              
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-20 h-20 mx-auto mb-6" style={{ color: '#D4AF37' }} />
                  <h3 className="text-3xl font-bold text-[#0A2846] mb-4">Vielen Dank!</h3>
                  <p className="text-xl text-[#1F2937]/80">Wir haben Ihre Anfrage erhalten und melden uns innerhalb von 24 Stunden bei Ihnen.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0A2846] font-semibold mb-2 block">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Ihr vollständiger Name"
                        className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-[#0A2846] font-semibold mb-2 block">Firma / Organisation</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Firmenname (optional)"
                        className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-[#0A2846] font-semibold mb-2 block">E-Mail-Adresse *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="ihre@email.de"
                        className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#0A2846] font-semibold mb-2 block">Telefonnummer *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+49 ..."
                        className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-[#0A2846] font-semibold mb-2 block">Objektadresse</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Straße, PLZ, Stadt"
                      className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-[#0A2846] font-semibold mb-2 block">Gewünschte Leistung *</Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-md focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
                    >
                      <option value="">Bitte wählen Sie eine Leistung</option>
                      <option value="buero">Büro- & Gewerbereinigung</option>
                      <option value="treppenhaus">Treppenhausreinigung</option>
                      <option value="fenster">Glas- & Fensterreinigung</option>
                      <option value="laden">Ladenflächen- & Gastronomiereinigung</option>
                      <option value="unterhalts">Unterhaltsreinigung</option>
                      <option value="winter">Winterdienst</option>
                      <option value="bau">Bau- & Renovierungsreinigung</option>
                      <option value="facility">Objektbetreuung & Facility Service</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0A2846] font-semibold mb-2 block">Ihre Nachricht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Beschreiben Sie Ihr Objekt und Ihre Anforderungen..."
                      className="border-2 border-gray-200 focus:border-[#D4AF37] transition-colors duration-300 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full font-semibold text-lg px-8 py-6 transition-all duration-500 hover:scale-105"
                    style={{
                      background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
                      color: '#0A2846',
                      boxShadow: '0 10px 40px rgba(212, 175, 55, 0.5), 0 0 50px rgba(212, 175, 55, 0.3)',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <Send className="w-5 h-5 mr-2 inline" />
                    {loading ? 'Wird gesendet...' : 'Anfrage absenden'}
                  </Button>

                  <p className="text-sm text-[#1F2937]/60 text-center mt-4">
                    * Pflichtfelder | Wir behandeln Ihre Daten vertraulich gemäß DSGVO
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6 order-1 lg:order-2">
            {/* Direct Call CTA */}
            <div 
              className="p-8 rounded-2xl shadow-elegant text-white relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0A2846 0%, #1a3a5a 100%)' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <Phone className="w-full h-full" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-4">Lieber direkt sprechen?</h3>
                <p className="text-[#E8EEF3]/90 mb-6 leading-relaxed">
                  Rufen Sie uns an – wir beraten Sie gerne persönlich und beantworten Ihre Fragen.
                </p>
                <a href="tel:+491701247477">
                  <Button
                    className="w-full font-semibold text-lg px-6 py-6 transition-all duration-500 hover:scale-105"
                    style={{
                      background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
                      color: '#0A2846',
                      boxShadow: '0 10px 40px rgba(212, 175, 55, 0.5)'
                    }}
                  >
                    <Phone className="w-5 h-5 mr-2 inline" />
                    +49 170 1247477
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white p-8 rounded-2xl shadow-elegant space-y-6">
              <h3 className="text-2xl font-bold text-[#0A2846] mb-6">Kontaktinformationen</h3>
              
              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #D4AF37, #FFD700)' }}
                >
                  <Mail className="w-6 h-6 text-[#0A2846]" />
                </div>
                <div>
                  <p className="text-sm text-[#1F2937]/60 mb-1">E-Mail</p>
                  <a href="mailto:kontakt@probav-reinigung.de" className="text-[#0A2846] font-semibold hover:text-[#D4AF37] transition-colors">
                    kontakt@probav-reinigung.de
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #D4AF37, #FFD700)' }}
                >
                  <Phone className="w-6 h-6 text-[#0A2846]" />
                </div>
                <div>
                  <p className="text-sm text-[#1F2937]/60 mb-1">Telefon</p>
                  <a href="tel:+491701247477" className="text-[#0A2846] font-semibold hover:text-[#D4AF37] transition-colors">
                    +49 170 1247477
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #D4AF37, #FFD700)' }}
                >
                  <Clock className="w-6 h-6 text-[#0A2846]" />
                </div>
                <div>
                  <p className="text-sm text-[#1F2937]/60 mb-1">Erreichbarkeit</p>
                  <p className="text-[#0A2846] font-semibold text-lg">24/7 für Sie da</p>
                  <p className="text-[#0A2846] text-sm">Jederzeit erreichbar</p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div 
              className="p-6 rounded-2xl text-center"
              style={{ 
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(255, 215, 0, 0.05))',
                border: '2px solid rgba(212, 175, 55, 0.2)'
              }}
            >
              <p className="text-[#0A2846] font-semibold text-lg mb-2">⚡ Schnelle Reaktion</p>
              <p className="text-[#1F2937]/70">Antwort innerhalb von 24 Stunden</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;